import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { UPCLAIM_ROLES } from "@upclaim/sdk/src/admin-roles";

import { Combobox } from "@/components/combobox";
import { useCurrentUser } from "@/hooks/use-current-user";
import { notify } from "@/lib/notify";
import { queryKeys } from "@/lib/query-keys";
import adminAPI from "@/services/api";

interface AdversaryOption {
  companyId: string;
  companyName: string;
  airlineNames: string[];
}

interface SetClaimAdversaryProps {
  claimId: string;
  currentAdversary?: { id?: string; name?: string | null } | null;
}

/**
 * The legal entity this claim is brought against.
 *
 * Distinct from the carrier that operated the flight: that is a fact about the flight, shared by
 * every claim on it, while the adversary is a decision taken per claim. Options are the companies
 * of the carriers on the reservation, deduplicated.
 */
export function SetClaimAdversary({ claimId, currentAdversary }: SetClaimAdversaryProps) {
  const queryClient = useQueryClient();
  const { can } = useCurrentUser();
  const canEdit = can([...UPCLAIM_ROLES, "LAWYER"]);

  const { data: options = [] } = useQuery({
    queryKey: [...queryKeys.claims.detail(claimId), "adversary-options"],
    queryFn: async () => {
      const { options } = await adminAPI.listClaimAdversaryOptions(claimId);
      return (options ?? []) as AdversaryOption[];
    },
    enabled: canEdit,
  });

  const setAdversary = useMutation({
    mutationFn: (option: AdversaryOption | null) =>
      notify.promise(adminAPI.updateClaim(claimId, { adversaryCompanyId: option?.companyId ?? null }), {
        success: "Adversaire enregistré",
      }),
    onSuccess: async ({ success }) => {
      if (success) {
        await queryClient.invalidateQueries({ queryKey: queryKeys.claims.detail(claimId) });
      }
    },
  });

  if (!canEdit) {
    return <span>{currentAdversary?.name ?? "Aucun"}</span>;
  }

  const selected = options.find((option) => option.companyId === currentAdversary?.id) ?? null;

  return (
    <Combobox
      items={options}
      value={selected}
      onChange={(option) => setAdversary.mutate(option)}
      getKey={(option) => option.companyId}
      getLabel={(option) => option.companyName}
      getSearchValue={(option) => option.airlineNames.join(" ")}
      placeholder={currentAdversary?.name ?? "Aucun"}
      searchPlaceholder="Rechercher..."
      emptyMessage="Aucune société liée au dossier"
      unselectLabel="Aucun"
    />
  );
}
